import React, { useEffect, useMemo, useState } from 'react';

/** Default MIME allowlist for new Google Drive folder groups (Zagger). */
export const DEFAULT_DRIVE_ALLOWED_TYPES = [
  'application/pdf',
  'application/vnd.google-apps.document',
  'application/vnd.google-apps.spreadsheet',
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/x-photoshop',
  'text/html',
  'text/plain',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.ms-powerpoint',
];

/** Default per-MIME processing mode overrides for new groups. */
export const DEFAULT_DRIVE_PROCESS_MODE_OVERRIDES = {
  'application/vnd.google-apps.spreadsheet': 'text',
  'text/plain': 'text',
  'text/html': 'text',
  'text/csv': 'text',
  'application/json': 'text',
};

// Google Drive Folder config editor
// props: value (config object), onChange(cfg)
const GoogleDriveFolderConfig = ({ value = {}, onChange }) => {
  // Single input: accepts either a full Google Drive folder link or a raw folder ID
  const initialInput = (value.folder_link || value.folder_id || '');
  const [folderInput, setFolderInput] = useState(initialInput);
  const [recursive, setRecursive] = useState(!!value.recursive);
  const [addDirectoryDepthColumns, setAddDirectoryDepthColumns] = useState(
    !!(value.add_directory_depth_columns ?? value.directory_depth?.enabled)
  );
  const [allowedTypes, setAllowedTypes] = useState(
    Array.isArray(value.allowed_types) ? value.allowed_types : [...DEFAULT_DRIVE_ALLOWED_TYPES]
  );
  const [defaultProcessMode, setDefaultProcessMode] = useState((value?.process_mode?.default) || 'file');
  const [overrides, setOverrides] = useState(
    value?.process_mode?.overrides || { ...DEFAULT_DRIVE_PROCESS_MODE_OVERRIDES }
  );
  // Ingestion mode: "add" (default) or "sync"
  const [mode, setMode] = useState(value.mode || 'add');

  // Keep input in sync when parent value changes (e.g., switching groups)
  useEffect(() => {
    const next = (value.folder_link || value.folder_id || '') || '';
    setFolderInput(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.folder_link, value.folder_id]);

  useEffect(() => {
    setAddDirectoryDepthColumns(
      !!(value.add_directory_depth_columns ?? value.directory_depth?.enabled)
    );
  }, [value.add_directory_depth_columns, value.directory_depth?.enabled]);

  // Parse folder input → derive ID and canonical link
  const { parsedId, canonicalLink } = useMemo(() => {
    const raw = (folderInput || '').trim();
    if (!raw) return { parsedId: '', canonicalLink: '' };
    // Helper to validate plausible Drive ID
    const isPlausibleId = (s) => {
      if (!s) return false;
      const okChars = /^[A-Za-z0-9-_]+$/;
      if (!okChars.test(s)) return false;
      return s.length >= 10 && s.length <= 200;
    };
    // Try URL parsing first
    try {
      const u = new URL(raw);
      let id = '';
      // Common patterns:
      // - /drive/folders/{id}
      // - /drive/u/0/folders/{id}
      // - /folders/{id}
      // - /open?id={id}
      const parts = u.pathname.split('/').filter(Boolean);
      const foldersIdx = parts.findIndex(p => p === 'folders');
      if (foldersIdx >= 0 && parts[foldersIdx + 1]) {
        id = parts[foldersIdx + 1];
      } else if (u.pathname.startsWith('/open')) {
        id = u.searchParams.get('id') || '';
      }
      if (!id) {
        // Some shared links might include ?id even on different paths
        const qid = u.searchParams.get('id');
        if (qid) id = qid;
      }
      if (isPlausibleId(id)) {
        return { parsedId: id, canonicalLink: `https://drive.google.com/drive/folders/${id}` };
      }
      // If it's a URL but we couldn't extract, fall back to raw as ID if plausible
      if (isPlausibleId(raw)) {
        return { parsedId: raw, canonicalLink: `https://drive.google.com/drive/folders/${raw}` };
      }
      return { parsedId: '', canonicalLink: '' };
    } catch (_) {
      // Not a URL; treat as raw ID if plausible
      if (isPlausibleId(raw)) {
        return { parsedId: raw, canonicalLink: `https://drive.google.com/drive/folders/${raw}` };
      }
      return { parsedId: '', canonicalLink: '' };
    }
  }, [folderInput]);

  // Sync outward
  useEffect(() => {
    const pm = { default: defaultProcessMode || 'file', overrides: overrides || {} };
    onChange && onChange({
      type: 'google_drive_folder',
      folder_link: canonicalLink || undefined,
      folder_id: parsedId || undefined,
      recursive: !!recursive,
      add_directory_depth_columns: !!recursive && addDirectoryDepthColumns,
      allowed_types: allowedTypes,
      mode: mode || 'add',
      process_mode: pm,
    });
  }, [
    canonicalLink,
    parsedId,
    recursive,
    addDirectoryDepthColumns,
    allowedTypes,
    defaultProcessMode,
    overrides,
    mode,
    onChange,
  ]);

  const canSave = useMemo(() => {
    return !!(parsedId && parsedId.trim());
  }, [parsedId]);

  const addAllowedType = (mt) => {
    const v = (mt || '').trim();
    if (!v) return;
    setAllowedTypes(prev => (prev.includes(v) ? prev : [...prev, v]));
  };
  const removeAllowedType = (mt) => {
    setAllowedTypes(prev => prev.filter(x => x !== mt));
  };

  const [newMime, setNewMime] = useState('');

  const setOverride = (mime, mode) => {
    setOverrides(prev => ({ ...(prev || {}), [mime]: mode }));
  };
  const removeOverride = (mime) => {
    setOverrides(prev => {
      const copy = { ...(prev || {}) };
      delete copy[mime];
      return copy;
    });
  };

  return (
    <div>
      <div className="form-row">
        <label>Google Drive Folder Link or ID</label>
        <input
          type="text"
          value={folderInput}
          onChange={e => setFolderInput(e.target.value)}
          placeholder="Paste a Drive folder link or enter the folder ID"
        />
        <div className="hint">Paste a link like https://drive.google.com/drive/folders/&lt;id&gt; or enter the raw folder ID. We’ll parse the link automatically.</div>
      </div>
      {parsedId ? (
        <div className="form-row">
          <div className="hint">Detected Folder ID: <code>{parsedId}</code></div>
          {canonicalLink && (
            <div className="hint">Canonical Link: <a href={canonicalLink} target="_blank" rel="noreferrer">{canonicalLink}</a></div>
          )}
        </div>
      ) : null}
      <div className="form-row">
        <label>Ingestion Mode</label>
        <select value={mode} onChange={e => setMode(e.target.value)}>
          <option value="add">add — add new files only</option>
          <option value="sync">sync — add new files and remove deleted ones</option>
        </select>
        <div className="hint">Controls behavior when ingesting this folder. "add" (default) only inserts new Drive files not already present in the group. "sync" also removes entries whose Drive files are no longer in the folder.</div>
      </div>
      <div className="form-row">
        <label>
          <input
            type="checkbox"
            checked={recursive}
            onChange={(e) => {
              const next = e.target.checked;
              setRecursive(next);
              if (!next) setAddDirectoryDepthColumns(false);
            }}
          />
          {' '}Include subfolders (recursive)
        </label>
      </div>
      <div className="form-row">
        <label className={recursive ? '' : 'drive-config-checkbox--disabled'}>
          <input
            type="checkbox"
            checked={addDirectoryDepthColumns}
            disabled={!recursive}
            onChange={(e) => setAddDirectoryDepthColumns(e.target.checked)}
          />
          {' '}Add columns for directory depth
        </label>
        <div className="hint">
          {recursive
            ? 'On ingest, scans the folder tree, names one column per depth level, and fills each file row with its folder path (shorter paths use null for deeper columns).'
            : 'Enable “Include subfolders (recursive)” first.'}
        </div>
      </div>

      <div className="form-row">
        <label>Allowed MIME Types</label>
        <div className="chips">
          {(allowedTypes || []).map(mt => (
            <span key={mt} className="chip">
              {mt}
              <button type="button" className="chip-remove" onClick={() => removeAllowedType(mt)} aria-label={`Remove ${mt}`}>
                ×
              </button>
            </span>
          ))}
        </div>
        <div className="inline-row">
          <input type="text" value={newMime} onChange={e => setNewMime(e.target.value)} placeholder="e.g. application/pdf" />
          <button type="button" className="btn" onClick={() => { addAllowedType(newMime); setNewMime(''); }}>Add</button>
        </div>
        <div className="hint">
          Defaults include PDF, Google Docs/Sheets, Word/PowerPoint, common images (JPEG, PNG, GIF), HTML, and plain text.
        </div>
      </div>

      <div className="form-row">
        <label>Default Processing Mode</label>
        <select value={defaultProcessMode} onChange={e => setDefaultProcessMode(e.target.value)}>
          <option value="file">file (store binary file)</option>
          <option value="text">text (store UTF-8 text)</option>
        </select>
        <div className="hint">Files default to file mode for PDFs, images, and Google Docs. Text formats and Sheets default to text.</div>
      </div>

      <div className="form-row">
        <label>Per-MIME Overrides</label>
        <div className="hint">Override the default processing mode for specific MIME types.</div>
        <div className="overrides">
          {Object.entries(overrides || {}).map(([mime, mode]) => (
            <div key={mime} className="override-row">
              <input type="text" value={mime} readOnly />
              <select value={mode} onChange={e => setOverride(mime, e.target.value)}>
                <option value="file">file</option>
                <option value="text">text</option>
              </select>
              <button type="button" className="btn" onClick={() => removeOverride(mime)}>Remove</button>
            </div>
          ))}
        </div>
        <div className="override-add inline-row">
          <input
            type="text"
            placeholder="MIME type (e.g. image/png)"
            value={newMime}
            onChange={e => setNewMime(e.target.value)}
          />
          <select onChange={e => setOverride(newMime || 'text/plain', e.target.value)} defaultValue="file">
            <option value="file">file</option>
            <option value="text">text</option>
          </select>
          <button type="button" className="btn" onClick={() => { if (newMime.trim()) setOverride(newMime.trim(), 'file'); setNewMime(''); }}>Add Override</button>
        </div>
      </div>

      {!canSave && (
        <div className="alert alert-info">
          Provide a valid Google Drive folder link or ID to save this configuration.
        </div>
      )}

      <div className="form-row">
        <div className="hint">
          Authentication uses your Personal Google Drive (User Settings). Configure OAuth app credentials there, connect
          Google, and ensure the Drive readonly scope is granted. Ingestion runs on-demand
          via the group ingest endpoint; background polling is off by default.
        </div>
      </div>

      <style>{`
        .chips { display: flex; flex-wrap: wrap; gap: 6px; }
        .chip { background: var(--glass-bg,#f2f4f7); color: var(--text-primary,#111); border: 1px solid var(--glass-border,#ddd); padding: 4px 8px; border-radius: 12px; display: inline-flex; align-items: center; gap: 6px; }
        .chip-remove { background: transparent; border: none; cursor: pointer; font-size: 14px; line-height: 1; }
        .inline-row { display: flex; gap: 8px; align-items: center; }
        .override-row { display: grid; grid-template-columns: 1fr 140px auto; gap: 8px; margin-bottom: 6px; }
        .drive-config-checkbox--disabled { opacity: 0.55; cursor: not-allowed; }
      `}</style>
    </div>
  );
};

export default GoogleDriveFolderConfig;
